import { useState, useEffect } from 'react';
import { Eye, FileText, Image, Loader2 } from 'lucide-react';
import { EntityModal } from '../common/EntityModal';
import { DocumentPreviewModal } from '../expenses/ExpenseDocumentsSection';
import type { PreviewableDocument } from './types';

export interface DocumentPreviewPickerProps {
  documents: PreviewableDocument[];
  isLoading?: boolean;
  onClose: () => void;
  title?: string;
  emptyMessage?: string;
}

/**
 * Read-only document viewer: lists the given documents, opens the selected one
 * in `DocumentPreviewModal`, and skips straight to the preview when there is
 * only one. Purely presentational — callers supply the documents, so the same
 * component serves producer views and the client portal.
 */
export function DocumentPreviewPicker({
  documents,
  isLoading = false,
  onClose,
  title = 'Documents',
  emptyMessage = 'No documents attached',
}: DocumentPreviewPickerProps) {
  const [previewDoc, setPreviewDoc] = useState<PreviewableDocument | null>(null);

  useEffect(() => {
    if (!isLoading && documents.length === 1 && !previewDoc) {
      setPreviewDoc(documents[0]);
    }
  }, [isLoading, documents, previewDoc]);

  if (previewDoc) {
    return (
      <DocumentPreviewModal
        document={previewDoc}
        onClose={() => {
          if (documents.length <= 1) onClose();
          else setPreviewDoc(null);
        }}
      />
    );
  }

  return (
    <EntityModal open onClose={onClose} title={title} size="xs" presentation="centered">
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 size={16} className="animate-spin text-primary-600" />
          <span className="text-sm text-gray-600">Loading...</span>
        </div>
      ) : documents.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">{emptyMessage}</p>
      ) : (
        <ul className="space-y-2" data-testid="doc-selector-list">
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer"
              onClick={() => setPreviewDoc(doc)}
            >
              {doc.mimeType.startsWith('image/') ? (
                <Image size={14} className="text-blue-500 flex-shrink-0" />
              ) : (
                <FileText size={14} className="text-red-500 flex-shrink-0" />
              )}
              <span className="text-sm text-gray-700 truncate flex-1">{doc.fileName}</span>
              <Eye size={14} className="text-gray-400 flex-shrink-0" />
            </li>
          ))}
        </ul>
      )}
    </EntityModal>
  );
}
